import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { loanApplicationSignalRConnection } from 'app/services/signalR';
import _ from 'lodash';

import { PresenceSignalRMessageType } from 'constants/presence';
import useLoanId from 'hooks/useLoanId';
import { logger } from 'utils/logger';

import {
  selectFieldLock,
  selectIsFieldLocked,
  selectIsFieldLockedByMe,
} from 'store/presence/selectors';

import { FIELD_LOCK_DEBOUNCE_MS } from './constants';

const useFieldLock = fieldPath => {
  const loanId = useLoanId();

  const normalizedPath = fieldPath?.toLowerCase();

  const lock = useSelector(selectFieldLock(normalizedPath));
  const isLockedByOther = useSelector(selectIsFieldLocked(normalizedPath));
  const isLockedByMe = useSelector(selectIsFieldLockedByMe(normalizedPath));

  const isLockedByAnotherUser = isLockedByOther && !isLockedByMe;

  const acquireLockRef = useRef(null);
  const releaseLockRef = useRef(null);
  const isLockedByMeRef = useRef(isLockedByMe);
  const loanIdRef = useRef(loanId);
  const normalizedPathRef = useRef(normalizedPath);

  useEffect(() => {
    isLockedByMeRef.current = isLockedByMe;
    loanIdRef.current = loanId;
    normalizedPathRef.current = normalizedPath;
  }, [isLockedByMe, loanId, normalizedPath]);

  const acquireLock = useCallback(() => {
    if (!loanId || !normalizedPath) {
      return;
    }

    if (acquireLockRef.current) {
      acquireLockRef.current.cancel();
    }

    acquireLockRef.current = _.debounce(async () => {
      try {
        await loanApplicationSignalRConnection.invoke(
          PresenceSignalRMessageType.ACQUIRE_FIELD_LOCK,
          loanId,
          normalizedPath,
          null,
        );
      } catch (err) {
        logger.error('Failed to acquire field lock', { loanId, fieldPath: normalizedPath, err });
      }
    }, FIELD_LOCK_DEBOUNCE_MS);

    acquireLockRef.current();
  }, [loanId, normalizedPath]);

  const releaseLock = useCallback(() => {
    if (!loanId || !normalizedPath) {
      return;
    }

    if (releaseLockRef.current) {
      releaseLockRef.current.cancel();
    }

    releaseLockRef.current = _.debounce(async () => {
      try {
        await loanApplicationSignalRConnection.invoke(
          PresenceSignalRMessageType.RELEASE_FIELD_LOCK,
          loanId,
          normalizedPath,
          null,
        );
      } catch (err) {
        logger.error('Failed to release field lock', { loanId, fieldPath: normalizedPath, err });
      }
    }, FIELD_LOCK_DEBOUNCE_MS);

    releaseLockRef.current();
  }, [loanId, normalizedPath]);

  useEffect(
    () => () => {
      if (acquireLockRef.current) {
        acquireLockRef.current.cancel();
      }

      if (releaseLockRef.current) {
        releaseLockRef.current.flush();

        return;
      }

      if (isLockedByMeRef.current && loanIdRef.current && normalizedPathRef.current) {
        loanApplicationSignalRConnection
          .invoke(
            PresenceSignalRMessageType.RELEASE_FIELD_LOCK,
            loanIdRef.current,
            normalizedPathRef.current,
            null,
          )
          .catch(err => {
            logger.error('Failed to release field lock on unmount', {
              loanId: loanIdRef.current,
              fieldPath: normalizedPathRef.current,
              err,
            });
          });
      }
    },
    [],
  );

  return {
    lock,
    isLockedByAnotherUser,
    isLockedByMe,
    acquireLock,
    releaseLock,
  };
};

export default useFieldLock;
